import { ReactNode, useCallback, useMemo, useState } from "react";
import { clsx } from "clsx";

import { AlertContext } from "../../lib/AlertContext";
import { AlertOptions } from "../../model/types/alert";
import { Alert } from "../Alert/Alert";

import { useDevice } from "@/shared/lib/hooks/useDevice";

import cls from "./AlertProvider.module.scss";

interface ThemeProviderProps {
  children: ReactNode;
}

const AlertProvider = ({ children }: ThemeProviderProps) => {
  const { isSmallScreen } = useDevice();

  const [alerts, setAlerts] = useState<AlertOptions[]>([]);

  const showAlert = useCallback((options: AlertOptions) => {
    const alert = { id: Date.now().toString(), ...options };

    if (typeof alert.message === "object") {
      alert.message = Object.values(alert.message).join(" ");
    }
    if (typeof alert.message !== "string") {
      return;
    }

    setAlerts((prev) => [{ ...alert }, ...prev]);
  }, []);

  const hideAlert = useCallback(
    (id: string) => () => {
      setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      showAlert,
    }),
    [showAlert]
  );

  return (
    <AlertContext.Provider value={contextValue}>
      <div className={clsx(cls.AlertProvider, { [cls.isMobile]: isSmallScreen })}>
        {alerts.map((alert) => (
          <Alert key={alert.id} data={alert} onHide={hideAlert(alert.id)}>
            {alert.message}
          </Alert>
        ))}
      </div>
      {children}
    </AlertContext.Provider>
  );
};

export default AlertProvider;
